import { AttachmentRequestStatusEnum } from "@/lib/enum/attachment-request-status.enum";
import { Filter, FilterConfig, FilterTypes, IsFilterable } from "@/lib/filterable";
import { CreateDto } from "@/lib/utility/data/create-dto.interface";
import { IEntity } from "@/lib/utility/data/entity.interface";
import { handleError } from "@/lib/utility/handleError";
import { SharingLinkFactory } from "@/lib/utility/sharingLinkFactory";
import { $t } from "@/lib/utility/t";
import attachmentService from "@/services/mrfiktiv/services/attachmentService";
import {
  MrfiktivAttachmentRequestStatusLogGen,
  MrfiktivAttachmentRequestViewModelGen,
  MrfiktivBaseCreateSharedContentDtoGen,
  MrfiktivCreateReportAsPartnerDtoGen,
  MrfiktivShortUserViewModelGen,
  MrfiktivTimestampGen
} from "@/services/mrfiktiv/v1/data-contracts";
import { ReportScreenEnum } from "@/store/enum/partner/report-screen.enum";
import { AttachmentRequestDataAccessLayer } from "@/store/modules/access-layers/attachment-request.access-layer";
import Vue from "vue";
import { IReference, Reference } from "./reference.entity";

@IsFilterable
class AttachmentRequestEntity
  extends CreateDto<IAttachmentRequest>
  implements MrfiktivAttachmentRequestViewModelGen, Partial<IEntity<IAttachmentRequest>>
{
  /**
   * Screens that should not be part of the attachment request
   */
  static get INVALID_SCREENS() {
    return [
      ReportScreenEnum.closing,
      ReportScreenEnum.closingalt,
      ReportScreenEnum.closingsimple,
      ReportScreenEnum.closingaltauth,
      ReportScreenEnum.closingauth,
      ReportScreenEnum.welcome,
      ReportScreenEnum.welcomealt,
      ReportScreenEnum.welcomelogin,
      ReportScreenEnum.REPAREO_HOME,
      ReportScreenEnum.REPAREO_CLOSING
    ];
  }

  id: string;

  partnerId: string;

  @FilterConfig({
    type: FilterTypes.OBJECT_ID,
    displayName: "objects.attachmentRequest.userId"
  })
  userId?: string | undefined;

  @FilterConfig({ type: Reference })
  refs: MrfiktivBaseCreateSharedContentDtoGen[];

  @FilterConfig({
    type: FilterTypes.DATE,
    displayName: "objects.attachmentRequest.validBy"
  })
  validBy?: string | null;

  token: string;

  @FilterConfig({
    type: FilterTypes.BOOLEAN,
    displayName: "objects.attachmentRequest.isSystemGenerated"
  })
  isSystemGenerated?: boolean | undefined;

  timestamp: MrfiktivTimestampGen;

  screenOrder: ReportScreenEnum[];

  attachments?: MrfiktivCreateReportAsPartnerDtoGen[];

  isLoading = false;

  isNotFound = false;

  title = "";

  description = "";

  reportId?: string | null | undefined;
  personIds?: string[] | null | undefined;
  companyIds?: string[] | null | undefined;
  emailAddresses?: null | string[];

  user?: MrfiktivShortUserViewModelGen | undefined;

  @FilterConfig({
    type: FilterTypes.ENUM,
    displayName: "objects.attachmentRequest.status",
    config: {
      items: Object.values(AttachmentRequestStatusEnum).map((status) => ({
        value: status,
        text: `enums.AttachmentRequestStatusEnum.${status}`
      })),
      itemValue: "value"
    }
  })
  status: AttachmentRequestStatusEnum = AttachmentRequestStatusEnum.CREATED;
  logs: MrfiktivAttachmentRequestStatusLogGen[] = [];

  get isExpired() {
    if (!this.validBy) return false;

    return new Date(this.validBy) < new Date();
  }

  get isUpdateable() {
    return this.status !== AttachmentRequestStatusEnum.COMPLETED;
  }

  constructor(attachmentRequest?: Partial<AttachmentRequestEntity | MrfiktivAttachmentRequestViewModelGen>) {
    super();
    this.id = attachmentRequest?.id || "";
    this.partnerId = attachmentRequest?.partnerId || "";
    this.userId = attachmentRequest?.userId || "";
    this.reportId = attachmentRequest?.reportId;
    this.refs = Reference.filterDuplicates((attachmentRequest?.refs ?? []).map((ref) => new Reference(ref)));
    this.validBy = attachmentRequest?.validBy || null;
    this.token = attachmentRequest?.token || "";
    this.isSystemGenerated = attachmentRequest?.isSystemGenerated || false;
    this.timestamp = (attachmentRequest?.timestamp || {}) as MrfiktivTimestampGen;
    this.screenOrder = ((attachmentRequest?.screenOrder as ReportScreenEnum[]) ?? []).filter(
      (screen) => !AttachmentRequestEntity.INVALID_SCREENS.includes(screen)
    );

    this.attachments = (attachmentRequest as AttachmentRequestEntity)?.attachments || [];
    this.title = attachmentRequest?.title || $t("objects.attachmentRequest.title");
    this.description = attachmentRequest?.description || "";
    this.user = attachmentRequest?.user;
    this.status = (attachmentRequest?.status as AttachmentRequestStatusEnum) ?? AttachmentRequestStatusEnum.CREATED;
    this.logs = attachmentRequest?.logs ?? [];
    this.personIds = attachmentRequest?.personIds || [];
    this.companyIds = attachmentRequest?.companyIds || [];
    this.emailAddresses = attachmentRequest?.emailAddresses;
  }

  private map(attachmentRequest: MrfiktivAttachmentRequestViewModelGen) {
    this.id = attachmentRequest.id;
    this.partnerId = attachmentRequest.partnerId;
    this.userId = attachmentRequest.userId;
    this.reportId = attachmentRequest?.reportId;
    this.refs = Reference.filterDuplicates((attachmentRequest?.refs ?? []).map((ref) => new Reference(ref)));
    this.validBy = attachmentRequest.validBy || null;
    this.token = attachmentRequest.token;
    this.isSystemGenerated = attachmentRequest.isSystemGenerated;
    this.timestamp = attachmentRequest.timestamp;
    this.screenOrder = ((attachmentRequest?.screenOrder as ReportScreenEnum[]) ?? []).filter(
      (screen) => !AttachmentRequestEntity.INVALID_SCREENS.includes(screen)
    );
    this.attachments = attachmentRequest.attachments;
    this.title = attachmentRequest.title || $t("objects.attachmentRequest.title");
    this.description = attachmentRequest.description || "";
    this.user = attachmentRequest?.user;
    this.status = (attachmentRequest?.status as AttachmentRequestStatusEnum) ?? AttachmentRequestStatusEnum.CREATED;
    this.logs = attachmentRequest?.logs ?? [];
    this.personIds = attachmentRequest?.personIds || [];
    this.companyIds = attachmentRequest?.companyIds || [];
    this.emailAddresses = attachmentRequest?.emailAddresses;
  }

  async fetch(silent?: boolean): Promise<this> {
    try {
      this.isLoading = true;
      const attachmentRequest = await attachmentService.getRequest(this.partnerId, this.id);
      this.map(attachmentRequest);
      AttachmentRequestDataAccessLayer.set(this);
    } catch (e) {
      if (silent) Vue.$log.error(e);
      else handleError(e);
      this.isNotFound = true;
    } finally {
      this.isLoading = false;
    }

    return this;
  }

  async update(silent = false): Promise<IAttachmentRequest> {
    try {
      this.isLoading = true;
      const attachmentRequest = await attachmentService.update(this.partnerId, this.id, {
        validBy: this.validBy || null,
        title: this.title,
        description: this.description,
        status: this.status,
        screenOrder: this.screenOrder.filter((screen) => !AttachmentRequestEntity.INVALID_SCREENS.includes(screen)),
        reportId: this.reportId,
        personIds: this.personIds,
        companyIds: this.companyIds,
        emailAddresses: this.emailAddresses
      });
      this.map(attachmentRequest);
      AttachmentRequestDataAccessLayer.set(this);
    } catch (e) {
      handleError(e);
      if (!silent) throw e;
    } finally {
      this.isLoading = false;
    }

    return this;
  }

  async create(silent = true): Promise<this> {
    try {
      const res = await attachmentService.create(this.partnerId, {
        title: this.title,
        description: this.description,
        refs: Reference.filterDuplicates(this.refs as IReference[]),
        screenOrder: this.screenOrder.filter((screen) => !AttachmentRequestEntity.INVALID_SCREENS.includes(screen)),
        validBy: this.validBy || null,
        emailAddresses: this.emailAddresses,
        companyIds: this.companyIds,
        personIds: this.personIds,
        reportId: this.reportId
      });

      const created = new AttachmentRequest(res);

      this.map(created);

      AttachmentRequestDataAccessLayer.set(this);
    } catch (e) {
      handleError(e);

      if (!silent) throw e;
    }

    return this;
  }

  async disable() {
    this.validBy = new Date().toISOString();

    await this.update();
  }

  async delete() {
    try {
      this.isLoading = true;
      await attachmentService.delete(this.partnerId, this.id);
    } catch (e) {
      handleError(e);
    } finally {
      this.isLoading = false;
    }
  }

  copyLinkToClipboard(): void {
    try {
      new SharingLinkFactory(this, true).copyLinkToClipboard();
      Vue.$toast.info($t("components.partner.PartnerReportInitializeCard.linkCopied"));
    } catch (error) {
      Vue.$log.error(error);
      Vue.$toast.error($t("components.partner.PartnerReportInitializeCard.error"));
    }
  }

  get isCompleted() {
    return [AttachmentRequestStatusEnum.COMPLETED].includes(this.status);
  }
}
type IAttachmentRequest = AttachmentRequestEntity;
const AttachmentRequest = Filter.createForClass(AttachmentRequestEntity);

export { AttachmentRequest, IAttachmentRequest };
